// Typed fetch wrapper for /api/interview. Mirrors the failure-semantics
// contract: every call returns either a parsed InterviewOpResult or a
// typed error that the UI can surface as a banner.

import type {
  InterviewAnswerRequest,
  InterviewClarifyRequest,
  InterviewOpResult,
  InterviewRegenerateRequest,
  InterviewSession,
  InterviewStartRequest,
} from '../types/interview';

interface ErrorBody {
  error: string;
  code?: 'PLAN_LIMIT' | 'UPGRADE_REQUIRED' | 'TIMEOUT' | 'BAD_REQUEST';
}

class InterviewApiError extends Error {
  code?: 'PLAN_LIMIT' | 'UPGRADE_REQUIRED' | 'TIMEOUT' | 'BAD_REQUEST';
  constructor(message: string, code?: 'PLAN_LIMIT' | 'UPGRADE_REQUIRED' | 'TIMEOUT' | 'BAD_REQUEST') {
    super(message);
    this.code = code;
  }
}

async function post(body: object): Promise<InterviewOpResult> {
  const response = await fetch('/api/interview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await response.json()) as InterviewOpResult | ErrorBody;
  if (!response.ok) {
    const err = data as ErrorBody;
    throw new InterviewApiError(err.error || `Request failed: ${response.status}`, err.code);
  }
  return data as InterviewOpResult;
}

export async function startInterview(req: InterviewStartRequest): Promise<InterviewOpResult> {
  return post({ op: 'start', payload: req });
}

export async function answerInterview(req: InterviewAnswerRequest): Promise<InterviewOpResult> {
  return post({ op: 'answer', sessionId: req.sessionId, payload: { text: req.text, direction: req.direction } });
}

export async function clarifyInterview(req: InterviewClarifyRequest): Promise<InterviewOpResult> {
  return post({ op: 'clarify', sessionId: req.sessionId, payload: { questionId: req.questionId } });
}

export async function regenerateInterview(req: InterviewRegenerateRequest): Promise<InterviewOpResult> {
  return post({ op: 'regenerate', sessionId: req.sessionId, payload: { direction: req.direction } });
}

export async function nextInterview(sessionId: string, direction?: string): Promise<InterviewOpResult> {
  return post({ op: 'next', sessionId, payload: { direction } });
}

export async function endInterview(sessionId: string, reason: 'complete' | 'abandoned' = 'complete'): Promise<InterviewOpResult> {
  return post({ op: 'end', sessionId, payload: { reason } });
}

export async function fetchInterview(sessionId: string): Promise<{ session: InterviewSession; transcript: any[] }> {
  const response = await fetch(`/api/interview?sessionId=${encodeURIComponent(sessionId)}`);
  const data = await response.json();
  if (!response.ok) {
    throw new InterviewApiError(data.error || `Request failed: ${response.status}`);
  }
  return data as { session: InterviewSession; transcript: any[] };
}

export async function listInterviewSessions(): Promise<{ sessions: InterviewSession[] }> {
  const response = await fetch('/api/interview');
  const data = await response.json();
  if (!response.ok) {
    throw new InterviewApiError(data.error || `Request failed: ${response.status}`);
  }
  return data as { sessions: InterviewSession[] };
}

export { InterviewApiError };
